import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import SearchField from 'src/shared/components/form/searchField';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { getAllEvents } from 'src/shared/services/eventService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { District } from 'src/shared/models/district/district';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { ControlledMultiDistrictPicker } from 'src/shared/components/form/controlledMultiDistrictPicker';
import { Box } from '@mui/material';
import { EventListCalendar } from '../components/eventListCalendar';
import { EventDateFilter, EventSearchFilter, EventSearchState } from '../models/eventSearchFilter';
import { Event } from '../models/event';

export const EventCalendarPage = () => {
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const [loading, setLoading] = useState<boolean>(true);
  const [event, setEvent] = useState<Event[]>([]);

  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [eventSearchFilter, setEventSearchFilter] = useLocalStorage<EventSearchState>(LocalStorageKeys.EventSearchFilter, {
    districtIds: undefined,
    searchString: '',
  });
  const [eventDateFilter, setEventDateFilter] = useLocalStorage<EventDateFilter>(LocalStorageKeys.EventDateFilterCalendar, {
    startDate: undefined,
    endDate: undefined,
  });

  const formMethods = useForm({
    defaultValues: {
      districtIds: eventSearchFilter.districtIds || undefined,
    },
  });

  const { handleSubmit } = formMethods;

  const memoizedSearchFilter: EventSearchFilter = useMemo(() => {
    return {
      associationIds: eventSearchFilter.districtIds
        ? eventSearchFilter.districtIds
            .filter((x) => !x.isParent)
            .map((x) => x.code)
            .map(Number)
        : undefined,
      districtIds: eventSearchFilter.districtIds
        ? eventSearchFilter.districtIds
            .filter((x) => x.isParent)
            .map((x) => x.code)
            .map(Number)
        : undefined,
      searchString: eventSearchFilter?.searchString,
    };
  }, [eventSearchFilter.districtIds, eventSearchFilter?.searchString]);

  const memoizedDateFilter: EventDateFilter = useMemo(() => {
    return {
      startDate: eventDateFilter?.startDate,
      endDate: eventDateFilter?.endDate,
    };
  }, [eventDateFilter.endDate, eventDateFilter.startDate]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setEventSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setEventSearchFilter((prev) => ({ ...prev, ...{ searchString: tempSearchString } }));
      } else {
        setEventSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
    }
  }, [isMounted, searchInitialized, setEventSearchFilter, tempSearchString]);

  const fetchData = useCallback(async () => {
    try {
      const res = await getAllEvents(0, 1000, 'date', 'asc', memoizedSearchFilter, memoizedDateFilter);
      if (isMounted()) {
        setEvent(res.data.results);
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted, memoizedSearchFilter, memoizedDateFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDistrict = useCallback(
    (x: District[] | undefined) => {
      if (x && x.length === 0) {
        setEventSearchFilter((prev) => ({
          ...prev,
          districtIds: undefined,
        }));
        return;
      }

      setEventSearchFilter((prev) => ({
        ...prev,
        districtIds: x,
      }));
    },
    [setEventSearchFilter],
  );

  const onSubmit = (data, e) => {
    e.preventDefault();
    ConsoleHelper.log('data', data);
    setEventSearchFilter(data);
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <PageFilterSection
            leftContent={
              <SearchField
                initialValue={eventSearchFilter.searchString || ''}
                onSearch={(newSearchString) => {
                  setSearchInitialized(true);
                  setTempSearchString(newSearchString);
                }}
              />
            }
            centerContent={<Box width={179} />}
            rightContent={currentUser ? <ControlledMultiDistrictPicker name='districtIds' callback={(x) => handleDistrict(x)} /> : null}
          />
        </form>
      </FormProvider>
      <EventListCalendar
        loading={loading}
        data={event}
        initialDate={eventDateFilter?.startDate ? dayjs(eventDateFilter?.startDate).format('YYYY-MM-DD') : undefined}
        dateCallback={(from: string, to: string) =>
          setEventDateFilter((prev) => {
            if (prev.startDate !== from) {
              return { ...prev, startDate: from || undefined, endDate: to || undefined };
            }
            return prev;
          })
        }
      />
    </>
  );
};
